import { Cell, Color, BotDifficulty, GameState, RingSize, SIZES, COLORS } from './types';
import { canPlace, placePiece } from './game-engine';

export interface BotMove {
  cellIndex: number;
  size: RingSize;
}

const LINES: [number, number, number][] = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6],
];

const STAIRCASES: [RingSize, RingSize, RingSize][] = [
  ['small', 'medium', 'large'],
  ['large', 'medium', 'small'],
];

function getValidMoves(state: GameState): BotMove[] {
  const { board, players, currentPlayerIndex } = state;
  const player = players[currentPlayerIndex];
  const moves: BotMove[] = [];
  for (const size of SIZES) {
    if (player.pieces[size] > 0) {
      for (let i = 0; i < 9; i++) {
        if (canPlace(board, i, size)) {
          moves.push({ cellIndex: i, size });
        }
      }
    }
  }
  return moves;
}

function wouldWin(state: GameState, move: BotMove): boolean {
  const newState = placePiece(state, move.cellIndex, move.size);
  return newState.winner !== null;
}

/** Find any single move that causes `color` to win, given their current pieces. */
function findWinningMoveForColor(state: GameState, color: Color): BotMove | null {
  const playerIdx = state.players.findIndex(p => p.color === color);
  if (playerIdx === -1) return null;
  const simState: GameState = { ...state, currentPlayerIndex: playerIdx };
  const moves = getValidMoves(simState);
  for (const move of moves) {
    if (wouldWin(simState, move)) return move;
  }
  return null;
}

/**
 * Score a board position for `color`.
 * 2-piece open lines (immediate threats) score 10 each.
 * 1-piece open lines (potential future threats) score 1 each.
 * This encourages the hard bot to build a broad web of threats.
 */
function scoreThreatWeb(board: Cell[], color: Color): number {
  let score = 0;

  for (const line of LINES) {
    const [a, b, c] = line;

    // Same-size lines
    for (const size of SIZES) {
      const vals = [board[a][size], board[b][size], board[c][size]];
      const myCount = vals.filter(v => v === color).length;
      const blocked = vals.some(v => v !== null && v !== color);
      if (!blocked) {
        if (myCount === 2) score += 10;
        else if (myCount === 1) score += 1;
      }
    }

    // Staircase lines
    for (const [sa, sb, sc] of STAIRCASES) {
      const vals = [board[a][sa], board[b][sb], board[c][sc]];
      const myCount = vals.filter(v => v === color).length;
      const blocked = vals.some(v => v !== null && v !== color);
      if (!blocked) {
        if (myCount === 2) score += 10;
        else if (myCount === 1) score += 1;
      }
    }
  }

  // Tower
  for (let i = 0; i < 9; i++) {
    const cell = board[i];
    const vals = [cell.small, cell.medium, cell.large];
    const myCount = vals.filter(v => v === color).length;
    const blocked = vals.some(v => v !== null && v !== color);
    if (!blocked) {
      if (myCount === 2) score += 10;
      else if (myCount === 1) score += 1;
    }
  }

  return score;
}

/** Count lines where `color` has exactly 2 pieces and the third slot is open. */
function countActiveThreats(board: Cell[], color: Color): number {
  return Math.floor(scoreThreatWeb(board, color) / 10);
}

/** Find a move for `color` that would create 2 or more active threats (a fork). */
function findForkMoveForColor(state: GameState, color: Color): BotMove | null {
  const playerIdx = state.players.findIndex(p => p.color === color);
  if (playerIdx === -1) return null;
  const simState: GameState = { ...state, currentPlayerIndex: playerIdx };
  const moves = getValidMoves(simState);
  for (const move of moves) {
    const newBoard = state.board.map((cell, i) =>
      i === move.cellIndex ? { ...cell, [move.size]: color } : cell
    );
    if (countActiveThreats(newBoard, color) >= 2) return move;
  }
  return null;
}

export function getBotMove(state: GameState, difficulty: BotDifficulty): BotMove {
  const { players, currentPlayerIndex, board } = state;
  const currentPlayer = players[currentPlayerIndex];
  const moves = getValidMoves(state);

  if (moves.length === 0) throw new Error('No valid moves for bot');

  const opponents = players.filter((_, i) => i !== currentPlayerIndex);

  if (difficulty === 'easy') {
    // Easy bots simulate a beginner: they miss wins, miss blocks, and otherwise play randomly.

    // Step 1: Take a winning move (but miss ~30% of the time)
    if (Math.random() > 0.30) {
      for (const move of moves) {
        if (wouldWin(state, move)) return move;
      }
    }

    // Step 2: Block any opponent's immediate win (miss ~55% of the time)
    for (const opp of opponents) {
      const winMove = findWinningMoveForColor(state, opp.color);
      if (winMove && Math.random() > 0.55) {
        const blockMove = moves.find(m => m.cellIndex === winMove.cellIndex && m.size === winMove.size);
        if (blockMove) return blockMove;
      }
    }

    // Step 3: Play randomly — no strategy, just like a beginner.
    return moves[Math.floor(Math.random() * moves.length)];
  } else {
    // Hard: block immediate wins, then fork, then block opponent forks,
    // then maximise the full threat web (1-piece + 2-piece lines weighted).

    // Step 2: Block any opponent's immediate win
    for (const opp of opponents) {
      const winMove = findWinningMoveForColor(state, opp.color);
      if (winMove) {
        const blockMove = moves.find(m => m.cellIndex === winMove.cellIndex && m.size === winMove.size);
        if (blockMove) return blockMove;
      }
    }

    // Step 3: Find a move that creates a fork (2+ immediate threats for self)
    const myFork = findForkMoveForColor(state, currentPlayer.color);
    if (myFork) return myFork;

    // Step 4: Block opponent forks
    for (const opp of opponents) {
      const oppFork = findForkMoveForColor(state, opp.color);
      if (oppFork) {
        const blockMove = moves.find(m => m.cellIndex === oppFork.cellIndex && m.size === oppFork.size);
        if (blockMove) return blockMove;
      }
    }

    // Step 5: Play the move that maximises the full threat web score
    let bestMove = moves[Math.floor(Math.random() * moves.length)];
    let bestScore = -1;
    for (const move of moves) {
      const newBoard = board.map((cell, i) =>
        i === move.cellIndex ? { ...cell, [move.size]: currentPlayer.color } : cell
      );
      const score = scoreThreatWeb(newBoard, currentPlayer.color);
      if (score > bestScore) {
        bestScore = score;
        bestMove = move;
      }
    }
    return bestMove;
  }
}
